<template>
  <section class="video-component">
    <video v-if="isMp4" :id="videoId" class="bg-video" playsinline autoplay muted loop subtitles="test">
      <source :src="videoSrc" type="video/mp4" />
      <track :label="description" kind="descriptions" srclang="en" />
    </video>

    <div v-if="isBackgroundVideo" ref="bgVideo" :data-video="videoId" class="bg-video">
      <div class="video-background">
        <div class="video-foreground" :style="foregroundStyles">
          <div :id="videoId"></div>
        </div>
      </div>
    </div>

    <div v-if="languageISO === 'zh_CN'">
      <image-component
        :id="fallbackImageData.id"
        :css-class="`${fallbackImageData.alignment} wallpaper fit-cover h-full`"
        :asset-id="fallbackImageData.assetId"
        img-format="pjpg"
        :is-data-object-fit="true"
        :alt-text="fallbackImageData.altText"
        transformation="fill"
        sizes="auto"
      />
    </div>
  </section>
</template>

<script>
import {mapState} from 'vuex';
import ImageComponent from '@/components/ImageComponent.vue';
import headTag from '@/utils/head-tag';
const WIDE_STYLES = {height: '300%', width: '100%', left: '0', top: '-100%'};
const NARROW_STYLES = {
  height: '100%',
  width: '300%',
  left: '-100%',
  top: '0',
};
const STANDARD_STYLES = {
  height: '100%',
  width: '100%',
  left: '0',
  top: '0',
};

export default {
  name: 'VideoComponent',
  components: {ImageComponent},
  props: {
    /**
     * Description for background videos not hosted on youtube.
     * Track element to address accessibility.
     */
    description: {
      type: String,
      default: '',
    },
    /**
     * Object to set a fallback image attrs
     * @params {id, assetId, alignment, altText}
     */
    fallbackImageData: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Sets video ID for external js.
     */
    videoId: {
      type: String,
      default: '',
      required: true,
    },
    /**
     * Url of the video source.
     */
    videoSrc: {
      type: String,
      default: '',
      required: true,
    },
  },
  data() {
    return {
      foregroundStyles: STANDARD_STYLES,
    };
  },
  computed: {
    ...mapState('backend', ['languageISO']),

    isMp4() {
      return this.videoSrc.lastIndexOf('.mp4') !== -1;
    },
    isBackgroundVideo() {
      return this.languageISO !== 'zh_CN' && this.videoSrc;
    },
  },
  mounted() {
    if (this.isBackgroundVideo) {
      headTag.appendScript('youtube-iframe-api', 'https://www.youtube.com/iframe_api');
      window.onYouTubeIframeAPIReady = function () {
        window.dispatchEvent(new CustomEvent('YouTubeIframeAPIReady', {}));
      };
      window.addEventListener('YouTubeIframeAPIReady', this.initializeVideoPlayer);
      window.addEventListener('resize', this.calculateStyles);
      this.calculateStyles();
    }
  },
  unmounted() {
    window.removeEventListener('YouTubeIframeAPIReady', this.initializeVideoPlayer);
    window.removeEventListener('resize', this.calculateStyles);
  },
  methods: {
    initializeVideoPlayer() {
      new window.YT.Player(this.videoId, {
        videoId: this.videoId,
        playerVars: {
          controls: 0,
          showinfo: 0,
          rel: 0,
          autoplay: 1,
          cc_load_policy: 0,
          playsinline: 1,
          loop: 1,
          mute: 1,
        },
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
        },
      });
    },
    calculateStyles() {
      if (this.$refs.bgVideo) {
        const width = this.$refs.bgVideo.offsetWidth;
        const height = this.$refs.bgVideo.offsetHeight;
        if (width / height > 16 / 9) {
          this.foregroundStyles = WIDE_STYLES;
        } else if (width / height < 16 / 9) {
          this.foregroundStyles = NARROW_STYLES;
        }
      }
      this.foregroundStyles = this.foregroundStyles || STANDARD_STYLES;
    },
    onPlayerReady(event) {
      this.calculateStyles();
      event.target.mute();
      event.target.playVideo();
    },

    onPlayerStateChange(event) {
      // This is to keep the video running after state change or switching apps on mobile too
      event.target.mute();
      event.target.playVideo();
    },
  },
};
</script>
